<template>
    <div class="trix-widget">
        <div
            :style="getStyles"
            class="bg-white rounded"
        >
            <div
                v-if="!editMode"
                :style="getStyles"
                v-html="fetchData"
            />
            <RedesignHtmlEditor
                v-else
                :edit-mode="editMode"
                :value="fetchData"
                @on-update="onUpdate"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import get from 'lodash/get'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { set } from 'vue'
import i18n from '@/i18n'
import { WidgetExtended } from '@/types/dashboard'
import { getAllDefaultCardColors } from '@/enum/defaultWidgetSettings'

const RedesignHtmlEditor = defineAsyncComponent(() => import('@/components/RedesignBaseComponents/RedesignHtmlEditor.vue'))

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
    }>(),
    {
        editable: false,
        onEditMode: false,
    }
)

const emit = defineEmits<{ (e: 'on-update', data: WidgetExtended): void }>()

const editMode = ref(false)

const fetchData = computed(() => {
    return props.data?.WidgetLayout?.htmlData ?? `<p>${i18n.t('widget.clickTheSwitchToEnableEditModeForThisHtml')}</p>`
})

const getStyles = computed(() => {
    const colors = get(props.data?.WidgetLayout, 'colors') ?? getAllDefaultCardColors()
    return {
        border: 0,
        backgroundColor: colors.background,
        color: colors.fonts,
        'margin-top': '10px',
    }
})

function onUpdate (val: string) {
    if (props.data?.WidgetLayout) {
        props.data.WidgetLayout = {
            ...props.data.WidgetLayout,
            htmlData: val,
        }
    }
    emit('on-update', props.data)
    editMode.value = false
}

onMounted(() => {
    if (props.data?.WidgetLayout && !props.data.WidgetLayout.hasOwnProperty('showLastUpdateDate')) {
        set(props.data.WidgetLayout, 'showLastUpdateDate', true)
    }
})

watch(() => props.editable, (state) => {
    editMode.value = state ?? false
})

watch(() => props.onEditMode, (state) => {
    editMode.value = state ?? false
}, { immediate: true })
</script>
<style lang="scss">
    .trix-widget {
        font-size: unset;
        font-weight: initial;
    }

    .el-switch.is-checked .el-switch__core {
        @apply border-primary bg-primary;
    }
</style>
